import { Button, DatePicker, Form, Input, message, Modal, Popconfirm, Select, Space, Spin, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { dictsDictionary2Options } from "@/extensions/dicts";
import { isMobile, menuSize } from "@/extensions/design";
import { fmtDate, fmtSyma, strDate2Server } from "@/extensions/utils";
import { serverFetch } from "@/server";
import Reestr from "./reestr";
import ReestrZag from "./reestr-zag";
import "./reestrs.css";

const Reestrs = () => {
  const user = useSelector((s) => s.user);
  const navigate = useNavigate();
  const { reestrId } = useParams();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentVids, setPaymentVids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [editId, setEditId] = useState();
  const [filter, setFilter] = useState({ dateFrom: dayjs().startOf("month"), dateTo: dayjs().endOf("month") });
  const [createForm] = Form.useForm();
  const divRef = useRef(null);

  const isSuperadmin = user?.isSuperadmin;
  const selectedId = Number(reestrId) || undefined;
  const createCompanyId = Form.useWatch("companyId", createForm);
  const createDateFrom = Form.useWatch("dateFrom", createForm);
  const createDateTo = Form.useWatch("dateTo", createForm);
  const createPaymentVidId = Form.useWatch("paymentVidId", createForm);
  const createOkDisabled = !createCompanyId || !createDateFrom || !createDateTo || !createPaymentVidId;

  useEffect(() => {
    if (isSuperadmin) {
      serverFetch("company")
        .then(data => setCompanies(dictsDictionary2Options(data)))
        .catch(e => message.error(e.userMessage));
    }
  }, [isSuperadmin]);

  useEffect(() => {
    setPaymentVids([]);
    createForm.setFieldValue("paymentVidId", undefined);
    if (!createCompanyId) return;

    serverFetch("PaymentVid", { queryParams: { companyId: createCompanyId } })
      .then(data => setPaymentVids(dictsDictionary2Options(data)))
      .catch(e => message.error(e.userMessage));
  }, [createCompanyId, createForm]);

  const getData = () => {
    setLoading(true);
    const queryParams = {
      DateFrom: strDate2Server(filter.dateFrom),
      DateTo: strDate2Server(filter.dateTo),
      CompanyId: filter.companyId,
    };
    serverFetch("Reestr", { queryParams })
      .then(data => setItems(data))
      .catch(e => message.error(e.userMessage))
      .finally(() => setLoading(false));
  };

  useEffect(getData, [filter.dateFrom, filter.dateTo, filter.companyId]);

  const openReestr = (record) => {
    navigate(`/reestrs/${record.id}`);
  };

  const closeReestr = (refresh = false) => {
    navigate("/reestrs");
    if (refresh) getData();
  };

  const onCreate = () => {
    createForm.validateFields()
      .then(values => {
        const bodyData = {
          companyId: values.companyId,
          paymentVidId: values.paymentVidId,
          dateFrom: strDate2Server(values.dateFrom),
          dateTo: strDate2Server(values.dateTo),
          comment: values.comment,
        };
        setLoading(true);
        serverFetch("Reestr/CreateByFilter", { method: "POST", bodyData })
          .then(data => {
            message.success("Реестр создан");
            setCreateVisible(false);
            createForm.resetFields();
            getData();
            openReestr(data);
          })
          .catch(e => message.error(e.userMessage))
          .finally(() => setLoading(false));
      });
  };

  const onDeleteHeader = (record) => {
    serverFetch(`Reestr/${record.id}`, { method: "DELETE" })
      .then(() => {
        message.success("Реестр удален");
        if (selectedId === record.id) closeReestr();
        getData();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onSign = (record) => {
    serverFetch(`Reestr/Sign/${record.id}`, { method: "POST" })
      .then(() => {
        message.success("Реестр подписан");
        getData();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onUnsign = (record) => {
    serverFetch(`Reestr/Unsign/${record.id}`, { method: "POST" })
      .then(() => {
        message.success("Подпись снята");
        getData();
      })
      .catch(e => message.error(e.userMessage));
  };

  const onEditHeaderClose = (refresh = false) => {
    setEditId();
    if (refresh) getData();
  };

  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - divRef.current.getBoundingClientRect().top - window.scrollY - 16) : 0;

  const renderActions = (record) => (
    <div className="reestr-row-actions" onClick={e => e.stopPropagation()}>
      <div className={selectedId === record.id ? "nav-menu-indicator" : "reestr-row-indicator-placeholder"} />
      <div className="reestr-row-action-buttons">
        {user?.isAdmin && (
          record.signed
            ? isSuperadmin
              ? (
                <Popconfirm title="Снять подпись с реестра?" onConfirm={() => onUnsign(record)}>
                  <img src="/images/close_square.svg" alt="Снять подпись" title="Снять подпись" className="sport-btn-icon" />
                </Popconfirm>
              )
              : <img src="/images/checkmark.svg" alt="Подписать" title="Реестр уже подписан" className="sport-btn-icon reestr-action-disabled" />
            : (
              <Popconfirm title="Подписать реестр?" onConfirm={() => onSign(record)}>
                <img src="/images/checkmark.svg" alt="Подписать" title="Подписать" className="sport-btn-icon" />
              </Popconfirm>
            )
        )}
        {isSuperadmin && !record.signed && <img src="/images/edit.svg" alt="Редактировать" title="Редактировать шапку" className="sport-btn-icon" onClick={() => setEditId(record.id)} />}
        {isSuperadmin && !record.signed && (
          <Popconfirm title="Удалить реестр?" onConfirm={() => onDeleteHeader(record)}>
            <img src="/images/trashcan.svg" alt="Удалить" title="Удалить" className="sport-btn-icon" />
          </Popconfirm>
        )}
      </div>
    </div>
  );

  const columns = [
    { title: "", key: "actions", width: 88, render: (_, record) => renderActions(record) },
    { title: "#", dataIndex: "id", width: 70 },
    { title: "Дата", dataIndex: "docDate", width: 110, render: fmtDate },
    { title: "Компания", dataIndex: "companyName", width: 360 },
    { title: "Сумма", dataIndex: "syma", width: 100, align: "right", render: fmtSyma },
    { title: "Оплата", dataIndex: "paymentDate", width: 110, render: fmtDate },
    { title: "Строк", dataIndex: "cntCount", width: 70, align: "right" },
    { title: "Статус", dataIndex: "signed", width: 120, render: value => value ? <Tag color="green">Подписан</Tag> : <Tag>Не подписан</Tag> },
    { title: "Комментарий", dataIndex: "comment" },
  ];

  return (
    <div className="col-one-main">
      <div className="col-one-title">
        <span className="col-one-title-item col-one-title-selected">Реестры оплат</span>
      </div>
      <div className="col-one-col">
        <div className="sport-form-content" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div className="sport-form-title">
            <span>Период</span>
            <DatePicker value={filter.dateFrom} format="DD.MM.YYYY" allowClear={false} onChange={value => setFilter({ ...filter, dateFrom: value })} />
            <DatePicker value={filter.dateTo} format="DD.MM.YYYY" allowClear={false} onChange={value => setFilter({ ...filter, dateTo: value })} />
            {isSuperadmin && <Select allowClear placeholder="Компания" options={companies} value={filter.companyId} onChange={value => setFilter({ ...filter, companyId: value })} style={{ minWidth: 220 }} />}
            <Button onClick={getData}>Обновить</Button>
            {isSuperadmin && <Button type="primary" onClick={() => setCreateVisible(true)}>Добавить</Button>}
          </div>

          <div ref={divRef} style={{ flex: 1 }}>
            {isMobile ? (
              <div style={{ height: window.innerHeight - menuSize - 90, overflowY: "auto" }}>
                {loading ? <Spin /> : items.map(item => (
                  <div key={item.id} className="stock-card" onClick={() => openReestr(item)}>
                    <div style={{ marginBottom: 8 }}>{renderActions(item)}</div>
                    <b>#{item.id} от {fmtDate(item.docDate)}</b>
                    <div>{item.companyName}</div>
                    <div>Сумма: {fmtSyma(item.syma)}</div>
                    <div>{item.signed ? "Подписан" : "Не подписан"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <Table className="reestrs-table" dataSource={items} columns={columns} rowKey="id" loading={loading} pagination={false} size="small" scroll={{ y: tableHeight }} onRow={record => ({ onClick: () => openReestr(record) })} />
            )}
          </div>

          {selectedId && <Reestr id={selectedId} onClose={closeReestr} onRefresh={getData} />}
          {editId && <ReestrZag id={editId} onClose={onEditHeaderClose} />}

          <Modal title="Создание реестра" open={createVisible} onOk={onCreate} onCancel={() => setCreateVisible(false)} okText="OK" cancelText="Отмена" okButtonProps={{ disabled: createOkDisabled }}>
            <Form form={createForm} layout="vertical" initialValues={{ dateFrom: dayjs().startOf("month"), dateTo: dayjs().endOf("month") }}>
              <Form.Item label="Компания" name="companyId" rules={[{ required: true, message: "Выберите компанию" }]}>
                <Select options={companies} showSearch optionFilterProp="label" />
              </Form.Item>
              <Space style={{ width: "100%" }} direction={isMobile ? "vertical" : "horizontal"}>
                <Form.Item label="Дата с" name="dateFrom" rules={[{ required: true, message: "Укажите дату начала" }]} style={{ width: "100%" }}>
                  <DatePicker format="DD.MM.YYYY" allowClear={false} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label="Дата по" name="dateTo" rules={[{ required: true, message: "Укажите дату окончания" }]} style={{ width: "100%" }}>
                  <DatePicker format="DD.MM.YYYY" allowClear={false} style={{ width: "100%" }} />
                </Form.Item>
              </Space>
              <Form.Item label="Вид платежа" name="paymentVidId" rules={[{ required: true, message: "Выберите вид платежа" }]}>
                <Select options={paymentVids} disabled={!createCompanyId} />
              </Form.Item>
              <Form.Item label="Комментарий" name="comment">
                <Input maxLength={255} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Reestrs;
